import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, ShieldCheck } from "lucide-react";

export function CarePlans({ projectId = null, compact = false, onSubscribed = null }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/care-plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    if (!plan.active) return;
    setBusy(plan.id);
    try {
      const { data } = await api.post("/subscriptions/subscribe", { plan_id: plan.id, project_id: projectId });
      if (onSubscribed) { onSubscribed(data); return; }
      const co = await api.post("/checkout/session", { origin_url: window.location.origin, kind: "invoice", invoice_id: data.invoice_id });
      window.location.href = co.data.url;
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.message("Please sign in to start a plan.");
        navigate("/login");
        return;
      }
      toast.error(formatApiError(e));
    } finally { setBusy(""); }
  };

  if (!plans.length) return null;

  return (
    <div data-testid="care-plans">
      {!compact && (
        <div className="mb-8">
          <div className="text-gold text-xs uppercase tracking-[0.25em] mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Hosting & Care</div>
          <h2 className="font-heading text-3xl sm:text-4xl text-white font-light">Keep it running &amp; growing</h2>
          <p className="text-gray-400 mt-3 max-w-2xl text-sm leading-relaxed">Your build launches your site. A monthly plan keeps it fast, secure, and improving — hosting, security, and hands-on care so you never touch the technical side.</p>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((plan, i) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            data-testid={`plan-${plan.id}`}
            className={`relative flex flex-col p-6 border ${plan.recommended ? "border-gold bg-gold/[0.06]" : "border-white/12 bg-navy-800"} ${!plan.active ? "opacity-70" : ""}`}
          >
            {plan.recommended && <div className="absolute -top-3 left-6 bg-gold text-navy text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">Most popular</div>}
            {plan.coming_soon && <div className="absolute -top-3 right-6 bg-white/10 text-gray-200 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border border-white/15">Coming soon</div>}
            <div className="flex items-center gap-2">
              {plan.ai && <Sparkles size={15} className="text-gold" />}
              <h3 className="font-heading text-xl text-white">{plan.name}</h3>
            </div>
            <p className="text-gray-400 text-xs mt-1 mb-4 min-h-[32px]">{plan.tagline}</p>
            <div className="mb-5">
              <span className="font-heading text-4xl text-white">${plan.price}</span>
              <span className="text-gray-500 text-sm">/mo</span>
            </div>
            <ul className="space-y-2.5 mb-6 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                  <Check size={15} className={`shrink-0 mt-0.5 ${plan.recommended ? "text-gold" : "text-green-400"}`} /> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => subscribe(plan)} disabled={!plan.active || busy === plan.id}
              data-testid={`subscribe-${plan.id}`}
              className={`w-full py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                !plan.active ? "bg-white/5 text-gray-500 cursor-not-allowed"
                : plan.recommended ? "bg-gold text-navy hover:bg-gold-light"
                : "border border-white/20 text-white hover:bg-white/5"}`}
            >
              {busy === plan.id ? <Loader2 className="animate-spin" size={15} /> : null}
              {plan.coming_soon ? "Coming soon" : `Start ${plan.name}`}
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
